// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartContainer.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

import React, { Component } from 'react';
import { reaction } from 'mobx';
import PropTypes from 'prop-types';
import connect from 'share/enhancers/Izenda.Connect';
import action from './Izenda.ReportPartContainerActions';

import AppContext from 'share/enhancers/Izenda.AppContext';
import ReportPartHeader from './Izenda.ReportPartHeader';
import DefaultReportPart from './Izenda.DefaultReportPart';
import ReportPartBackSide from './Izenda.ReportPartBackSide';
import ReportPartFrontSide from './Izenda.ReportPartFrontSide';
import ReportPartViewer from 'share/components/views/Izenda.ReportPartViewer';
import ReportDesignerConstants from 'logic/data/Izenda.ReportDesignerConstants';
import LoadingIndicator from 'share/components/controls/Izenda.LoadingIndicator';
import ReportDesignerWidget from 'share/components/hoc/Izenda.ReportDesignerWidget';
import registry from './ReportPartType/Izenda.ReportPartTypeRegistry';
import { get, invoke, isEmpty, isNil, set, unset, clone, isEqual, find, reduce } from 'lodash/fp';

const DEEP_COMPARE_PROPERTIES = ['style'];
const PADDING_SIZE = 10;
const DEFAULT_REPORT_BODY_HEIGHT = 38;

registry.initRegistry();

class ReportPartContainer extends Component {
  static propTypes = {
    store: PropTypes.object,
    gridItem: PropTypes.object.isRequired,
    reportBody: PropTypes.object,
    uiStore: PropTypes.object,
    minWidth: PropTypes.number,
    minHeight: PropTypes.number,
    autoSize: PropTypes.bool,
    isSnapEditOn: PropTypes.bool
  };

  destroyReaction = () => {
    invoke('disposeReaction', this);
  }

  initReaction = (props: any) => {
    const { switchToDesignReportContainer } = this.props.store.reportStore;
    this.destroyReaction();
    this.disposeReaction = reaction(
      () => get('reportPartContent', props.gridItem),
      () => {
        // use lasted props don't need to bind to current props object when create observe
        !switchToDesignReportContainer && props.loadReportPartData();
      }
    );
  }

  changeReportPartSide = () => {
    const { reportBody, gridItem, viewMode } = this.props;
    // if the page in view mode then report part should be front side
    if (viewMode && gridItem.isBackSide) {
      reportBody.changeReportPartSide(gridItem, false);
    }
  }

  componentWillMount() {
    const { reportStore } = this.props.store;

    if (!reportStore.switchToDesignReportContainer) {
      this.props.loadReportPartData();
      this.initReaction(this.props);
      this.changeReportPartSide();
    } else {
      reportStore.switchToDesignReportContainer = false;
    }
  }

  componentWillUnmount() {
    this.destroyReaction();
  }

  componentWillReceiveProps(nextProps: any) {
    const { switchToDesignReportContainer } = this.props.store.reportStore;

    // for full screen, if change config report part we need to load data
    if (this.props.gridItem !== nextProps.gridItem && !switchToDesignReportContainer) {
      nextProps.loadReportPartData();
      this.initReaction(nextProps);
    }
  }

  shouldComponentUpdate(nextProps: any) {
    const { width, height } = this.props;

    if (nextProps.width !== width || (nextProps.height !== height && height !== DEFAULT_REPORT_BODY_HEIGHT)) {
      return false;
    }

    const reduceWithKey = reduce.convert({ cap: false });
    // Reduce render for style object
    const result = reduceWithKey(
      (acc, value, key) => {
        const cur = get(key, acc);
        if (DEEP_COMPARE_PROPERTIES.indexOf(key) > -1 ? isEqual(cur, value) : cur === value) {
          return unset(key, acc);
        }
        return set(key, [key, cur], acc);
      },
      clone(nextProps),
      this.props
    );

    return !isEmpty(result);
  }

  setDom = (dom: any) => {
    this.containerRef = dom;
  }

  componentDidMount() {
    const { onMeasureChange, autoSize } = this.props;
    const { clientWidth, clientHeight } = this.containerRef;
    autoSize && onMeasureChange && onMeasureChange(clientWidth, clientHeight);
  }

  renderContent = (partProps: any, isDefault: boolean) => {
    const props = this.props;
    if (isDefault) {
      return (
        <DefaultReportPart
          {...partProps}
          beforeShowProps={props.beforeShowProps}
          propsEditors={ReportDesignerConstants.EDITOR_CONFIG.ReportPart}
        />
      );
    }

    return props.gridItem.isBackSide ? (
      <ReportPartBackSide {...partProps} stateReview={props.stateReview} />
    ) : (
      <ReportPartFrontSide {...partProps} />
    );
  }

  checkPrivileges = () => {
    const { gridItem, authorization } = this.props;

    const reportPartConfig = find(
      item => item.value === gridItem.type,
      ReportDesignerConstants.REPORT_PART_PERMISSIONS
    );

    if (!isNil(reportPartConfig) && reportPartConfig.permissions) {
      return authorization.checkPrivileges(reportPartConfig.permissions);
    }

    return true;
  }

  render() {
    const props = this.props;
    const {
      store,
      uiStore,
      viewMode,
      printingMode,
      quickEdit,
      reportBody = {},
      autoSize,
      minWidth,
      minHeight,
      loader,
      hideGridPopup
    } = props;
    const reportPart = props.gridItem;
    const reportPartId = reportPart.id;
    const hasPrivileges = this.checkPrivileges();
    const isDefault = isNil(reportPart.type);
    const partProps = {
      reportPart,
      reportBody,
      store,
      viewMode,
      quickEdit,
      hasPrivileges,
      autoSize,
      loader,
      hideGridPopup
    };

    const contentStyle = {
      ...(!reportPart.isLoadingData && { minWidth: minWidth - PADDING_SIZE || 0, minHeight: minHeight - PADDING_SIZE || 0 })
    };

    const header = {
      component: hasPrivileges ? ReportDesignerWidget.wrapper(ReportPartHeader) : ReportPartHeader
    };

    if (printingMode) {
      const reportId = store.reportId;

      return (
        <div id={reportPartId} ref={this.setDom} className="report-part u-bgWhite u_noPaddingAll">
          <div className="report-content u-bgWhite" style={contentStyle}>
            <ReportPartViewer
              reportPart={reportPart}
              store={store}
              reportPartId={reportPartId}
              reportId={reportId}
              exportFormatSetting={props.exportFormatSetting}
              printingMode={printingMode}
              hideGridPopup
              setting={props.setting}
              finishedRender={props.finishedRender}
              doesBreakPage={props.doesBreakPage}
              isFirstReportPart={props.isFirstReportPart}
            />
          </div>
          {props.pageBreakAfterReportPart && !props.isLastReportPart && <div className="u_alwaysBreakAfter" />}
        </div>
      );
    }

    return (
      <div id={reportPartId} ref={this.setDom} className="report-part" style={props.style}>
        <div className="report-content" style={contentStyle}>
          <header.component
            itemData={reportPart}
            reportPart={reportPart}
            reportBody={reportBody}
            store={store}
            uiStore={uiStore}
            viewMode={viewMode}
            quickEdit={quickEdit}
            propsEditors={ReportDesignerConstants.EDITOR_CONFIG.ReportPart}
            beforeShowProps={props.beforeShowProps}
            setStateReview={props.setStateReview}
            stateReview={props.stateReview}
            hasPrivileges={hasPrivileges}
            onChangeFullScreen={props.onChangeFullScreen}
            loadReportPartData={props.loadReportPartData}
          />
          {this.renderContent(partProps, isDefault)}
          {reportPart.type !== ReportDesignerConstants.REPORT_PART_TYPE.Chart && <LoadingIndicator />}
        </div>
      </div>
    );
  }
}

const partLoaders = {};
export default connect(
  {
    context: {
      childContextTypes: AppContext.loaderShape,
      getChildContext(props) {
        const reportPart = props.gridItem;

        if (!partLoaders[reportPart.id]) {
          partLoaders[reportPart.id] = AppContext.createLoaderContext();
        }
        return partLoaders[reportPart.id];
      }
    }
  },
  action
)(ReportPartContainer);



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/Izenda.ReportPartContainer.js